import { type Product, type ProductStatus } from '@/constants/products'
import { cn } from '@/lib/cn'

interface ProductSystemTabsProps {
  products: readonly Product[]
  selectedProductId: string
  onSelectProduct: (productId: string) => void
}

const statusDotMap: Record<ProductStatus, string> = {
  'Production Foundation': 'bg-success',
  'In Development': 'bg-info',
  'Planning Phase': 'bg-fg-muted',
  'Coming Soon': 'bg-warning',
}

export function ProductSystemTabs({
  products,
  selectedProductId,
  onSelectProduct,
}: ProductSystemTabsProps) {
  return (
    <div
      role="tablist"
      aria-label="HUN-TECH Platforms and Systems"
      className="flex items-center gap-2 overflow-x-auto no-scrollbar py-2 border-b border-border-subtle"
    >
      {products.map((product) => {
        const isSelected = product.id === selectedProductId
        const dotClass = statusDotMap[product.status]

        return (
          <button
            key={product.id}
            role="tab"
            id={`tab-${product.id}`}
            aria-selected={isSelected}
            aria-controls={`panel-${product.id}`}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => onSelectProduct(product.id)}
            className={cn(
              'group flex items-center gap-2 px-3.5 py-2 rounded-sm text-xs sm:text-sm font-medium tracking-tight transition-all duration-150 cursor-pointer select-none shrink-0 border',
              isSelected
                ? 'bg-btn-primary text-btn-primary-fg border-transparent shadow-xs'
                : 'bg-surface-2 text-fg-subtle border-border-subtle hover:text-fg hover:bg-surface-3 hover:border-border'
            )}
          >
            <span
              className={cn(
                'w-1.5 h-1.5 rounded-full shrink-0 transition-opacity',
                dotClass,
                isSelected ? 'opacity-100 ring-2 ring-btn-primary-fg/30' : 'opacity-70 group-hover:opacity-100'
              )}
              aria-hidden="true"
            />
            <span className="font-semibold">{product.name}</span>
            {product.amharicName && (
              <span
                className={cn(
                  'font-mono text-[11px] px-1.5 py-0.5 rounded-xs transition-colors',
                  isSelected
                    ? 'bg-btn-primary-fg/15 text-btn-primary-fg'
                    : 'bg-surface-3 text-fg-muted'
                )}
              >
                {product.amharicName}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
